import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { of } from 'rxjs';
import { MtmDriveRailComponent } from '@mtm/drive/components';
import { MTM_CONVERSATION_IDENTITY, type AccountRef, type ConversationIdentity } from '@mtm/media-studio/conversation';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { DriveRecentAiUiService } from '../drive-recent-ai-ui.service';
import { DriveRightRailService } from '../drive-right-rail.service';

/**
 * The drive surfaces' right rail as child-layout draws it: the library's `<mtm-drive-rail>`
 * (feed, tasks, Recent activity) fed by {@link DriveRightRailService}. Clicked Recent
 * activity entries go to `DriveRecentAiUiService`, whose registered drive surface
 * performs the navigation.
 */
@Component({
  selector: 'mtm-drive-right-rail',
  standalone: true,
  imports: [MtmDriveRailComponent],
  providers: [
    // child-layout lives in the ROOT injector, but the Recent activity panel's
    // conversation identity is only registered inside the lazy drive scopes
    // (`provideMtmDriveSurface()` in project-RootModule etc.) — without it
    // the panel dies with NG0201 on open. The identity is backed by the app's
    // own auth bridge rather than the lazy HTTP identity service — every listed
    // prompt's author IS the signed-in user, and the display-name derivation
    // matches `HttpConversationIdentity` (first+last, else nickname, else
    // account name, else email).
    {
      provide: MTM_CONVERSATION_IDENTITY,
      useFactory: (): ConversationIdentity => {
        const bridge = inject(MtmAuthBridgeService);
        return {
          currentUser: computed<AccountRef | null>(() => {
            const user = bridge.currentUser();
            if (!user) {
              return null;
            }
            const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
            return {
              accountEmail: user.email,
              accountUuid: user.accountUuid,
              displayName: fullName || user.nickName || user.accountName || user.email,
              avatarUrl: user.avatarUrl || null,
            };
          }),
          searchUsers: () => of([]),
        };
      },
    },
  ],
  // Projected popovers (the notification menu) anchor at the rail's top-left corner.
  template: `
    <mtm-drive-rail [outlet]="rail" (recentEntryClicked)="recentAiUi.openPrompt($event)" />
    <div class="drr-anchor"><ng-content /></div>
  `,
  styles: `
    :host {
      position: relative;
      display: block;
    }
    /* Just under the rail (mtm-drive-rail lifts itself to 1000): the notification menu's
       full-screen backdrop then dims the page — composer card and feed included — while
       the rail stays pressable. Zero-size, and the projected anchors take no room either
       (the notification anchor is hidden in its own stylesheet), so nothing sticks out past
       the window edge while the rail column is collapsed to nothing. Never clip it with
       overflow: iOS Safari composites the position: fixed popovers inside and clips them
       to this 0x0 box, so the notification menu never shows on iPhone. */
    .drr-anchor {
      position: absolute;
      z-index: 999;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriveRightRailComponent {
  protected readonly rail = inject(DriveRightRailService);
  protected readonly recentAiUi = inject(DriveRecentAiUiService);
}
